import useTicketManagers from "../../../Ticketing/useTicketManagers/useTicketManagers";
import { AutomationWizardStepProps } from "../../AutomationsUtils";
import "./IntegrationInput.scss";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import {
  RadioButtonsList,
  Loader,
  useIcon,
  AlertIndicator,
  alertTypes,
  REQUIRED_ERROR_MESSAGE,
} from "@SeemplicityDev/shared";
import { useCallback, useEffect, useMemo } from "react";
import {
  TicketProviders,
  TicketEndpointKeys,
  getKeysThatHoldsValue,
  ticketEndpointTypeEnum,
  ticketEndpointContextEnum,
} from "@SeemplicityDev/ticket-master";

export const INTEGRATIONS_SUMMARY_LABEL = {
  [ticketEndpointTypeEnum.REPORT]: "Notification System",
  [ticketEndpointTypeEnum.TICKET]: "Ticketing System",
  [ticketEndpointTypeEnum.INTERNAL]: "Internal System",
};

interface IntegrationType {
  name: string;
  label: string;
  value: string;
  friendlyName: string;
  mustHaveMapping?: boolean;
  mustHaveColumnsMapping?: boolean;
  type?: string; // ticketEndpointTypeEnum;
  leftImg?: string;
  providers?: Array<{
    name: string;
    id: string;
  }>;
  attachmentSupported?: boolean;
}

export interface IntegrationInputProps extends AutomationWizardStepProps {
  type: string; // ticketEndpointTypeEnum;
  fieldMappingId?: string;
  onConfigurationChange?: () => void;
  onFinalPhase?: (
    isFinal: boolean | undefined,
    hasMapping: boolean | undefined
  ) => void;
  emptyStateAlertContent?: React.ReactNode;
  onGetKeysProps?: (callback: () => void) => void;
  onLoaded?: () => void;
}

const IntegrationInput: React.FC<IntegrationInputProps> = ({
  type,
  isSummary = false,
  defaultValues,
  fieldMappingId,
  onConfigurationChange,
  onFinalPhase,
  emptyStateAlertContent,
  onGetKeysProps,
  onLoaded,
}) => {
  // useTicketManagers is used widely so currently is not repactored, but it is also returnes other types of providers,
  // so I changed the name to integrationTypes
  const { ticketManagers: integrationTypes, loading } = useTicketManagers({
    context: ticketEndpointContextEnum.RMQ,
    type: type,
  }) as { ticketManagers: IntegrationType[] | null; loading: boolean };

  useEffect(() => {
    if (!loading) {
      onLoaded?.();
    }
  }, [loading]);

  const { getSvg, getDataSourceIcon } = useIcon();
  const methods = useFormContext();
  const {
    control,
    setValue,
    watch,
    formState: { dirtyFields },
  } = methods;

  const selectedIntegrationType = useWatch({
    control,
    name: "integrationType",
  });

  // An automation being edited arrives with `fieldMappingId` pinned so its keys render with the
  // values it was saved with. Point it at a different integration and those saved keys describe the
  // wrong provider — Slack's `Channel` while Email is selected — so the pin has to be released.
  // Compared against `defaultValues` rather than tracked as a dirty flag: only an automation being
  // edited carries `integrationType`, so this is false throughout a create flow instead of being
  // tripped by `RadioButtonsList` auto-selecting the first option during its first render, and it
  // goes back to false — restoring the saved keys — if the user switches back.
  const hasChangedIntegration =
    !!selectedIntegrationType &&
    !!defaultValues?.integrationType &&
    selectedIntegrationType !== defaultValues.integrationType;

  const integrationTypesOptions = useMemo(() => {
    return integrationTypes?.map((integrationType) => ({
      label: integrationType.friendlyName,
      name: integrationType.name,
      value: integrationType.value,
      leftImg: getDataSourceIcon(integrationType?.name, "S") as
        | string
        | undefined,
    }));
  }, [integrationTypes, getDataSourceIcon]);

  const integrations = useMemo(() => {
    return (
      !!integrationTypes &&
      integrationTypes?.find(
        (integrationType) => integrationType.value === selectedIntegrationType
      )?.providers
    );
  }, [integrationTypes, selectedIntegrationType]);

  useEffect(() => {
    if (!!integrations && integrations?.length === 1) {
      setValue("ticketProvider", integrations[0].id);
    }
  }, [integrations, setValue]);

  const selectedIntegration = useWatch({
    control,
    name: "ticketProvider",
  });
  const selectedKeys = watch("keys", {});
  const keysThatHoldsValue = getKeysThatHoldsValue(selectedKeys);

  const handleTicketProviderChange = useCallback(() => {
    onConfigurationChange?.();
    setValue("keys", {});
  }, [onConfigurationChange, setValue]);

  const handleIntegrationTypeChange = useCallback(
    (value: string, onChange: (value: string) => void) => {
      onConfigurationChange?.();
      onChange(value);
      setValue("ticketProvider", null);
      setValue("keys", {});
    },
    [onConfigurationChange, setValue]
  );

  const getProvidersAndKeys = () => {
    return (
      <>
        {!!integrations && integrations.length > 1 && (
          <TicketProviders
            shouldUnregister
            viewMode={isSummary}
            withMarginBottom={false}
            keysObject={keysThatHoldsValue}
            providersOptions={integrations}
            formMethods={methods}
            onTicketProviderChange={handleTicketProviderChange}
            disabled={
              !!fieldMappingId && type === ticketEndpointTypeEnum.TICKET
            }
          />
        )}
        {!!selectedIntegration && (
          <TicketEndpointKeys
            fieldMappingId={
              // The two `dirtyFields` clauses are load-bearing but fragile, and they are only safe
              // because of two invariants in the *parents*: (1) `RadioButtonsList` self-selects its
              // first option during its first render without dirtying anything, so mount does not
              // look like a user edit; and (2) both wizard steps drive this form with
              // `useForm({ values })`, and RHF wipes dirty state whenever a parent re-feeds `values`
              // — so dirty state is only meaningful between re-feeds. `hasChangedIntegration` is
              // derived from `defaultValues` precisely because it must not depend on either. If a
              // wizard is ever refactored to `defaultValues` or to re-feed mid-step, re-check these
              // two clauses: getting them wrong pins the saved mapping to the wrong provider and the
              // keys of a switched-away integration come back.
              !hasChangedIntegration &&
              !dirtyFields?.ticketProvider &&
              !dirtyFields?.keys &&
              !!defaultValues &&
              !defaultValues.keys
                ? fieldMappingId
                : undefined
            }
            shouldUnregister
            viewMode={isSummary}
            onKeyChange={onConfigurationChange}
            onFinalPhase={onFinalPhase}
            ticketProviderId={selectedIntegration}
            keysObject={keysThatHoldsValue}
            formMethods={methods}
            onGetProps={onGetKeysProps}
            disabled={
              !!fieldMappingId && type === ticketEndpointTypeEnum.TICKET
            }
          />
        )}
      </>
    );
  };

  return loading ? (
    <Loader
      center
      img={getSvg("icon-spinner")}
      iconSize={"xxxxl"}
      data-testid={`${type}-system-step-loader`}
    />
  ) : (
    <div
      className={`integration-input-container  ${
        isSummary ? "is-summary" : ""
      }`}
      data-testid="integration-input-container"
    >
      {(!integrationTypes?.length ||
        (type === ticketEndpointTypeEnum.REPORT &&
          integrationTypes?.length === 1)) &&
        !isSummary && (
          <AlertIndicator
            wrapperClassName="empty-state-alert-wrapper"
            type={alertTypes.INFO}
            hasMaxWidth={false}
            content={emptyStateAlertContent}
          ></AlertIndicator>
        )}
      {!!integrationTypes && integrationTypes.length >= 1 && (
        <>
          <Controller
            name="integrationType"
            control={control}
            rules={{ required: REQUIRED_ERROR_MESSAGE }}
            render={({ field: { value, onChange, onBlur } }) => (
              <RadioButtonsList
                options={integrationTypesOptions}
                data-testid="integration-type"
                viewMode={isSummary}
                label={
                  isSummary
                    ? INTEGRATIONS_SUMMARY_LABEL[type as ticketEndpointTypeEnum]
                    : undefined
                }
                name="integrationType"
                onBlur={onBlur}
                direction="row"
                value={value}
                onChange={(val: string) =>
                  handleIntegrationTypeChange(val, onChange)
                }
                disabled={
                  !!fieldMappingId && type === ticketEndpointTypeEnum.TICKET
                }
              />
            )}
          />
          {isSummary ? (
            getProvidersAndKeys()
          ) : (
            <div className="integration-input-keys-container">
              {getProvidersAndKeys()}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default IntegrationInput;
